import { useAtomValue } from 'jotai'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import { useLocation, useNavigate } from 'react-router'
import { useTranslate } from '@repo/unicity'
import { localize } from '@/core/utils'
import { feelgreatSettingsAtom } from '@/core/feelgreat/feelgreat.store'
import facebookIcon from './assets/footer-facebook.svg'
import instagramIcon from './assets/footer-instagram.svg'
import pinterestIcon from './assets/footer-pinterest.svg'
import twitterIcon from './assets/footer-twitter.svg'
import unicityLogo from './assets/footer-unicity.svg'
import youtubeIcon from './assets/footer-youtube.svg'

type LocalText = { english?: string; native?: string }

type FooterLinkItem = {
    title?: LocalText
    usageType?: string
    externalLink?: LocalText
    internalLink?: LocalText
    path?: LocalText
    handleFunction?: LocalText
    openTarget?: string
}

type FooterQuickLinkGroup = {
    title?: LocalText
    items?: FooterLinkItem[]
}

type FooterConfigLink = {
    title?: LocalText
    externalLink?: LocalText
    openTarget?: string
}

type FooterSettings = {
    quickLinks?: { items?: FooterQuickLinkGroup[] }
    social?: Record<string, LocalText | boolean | null | undefined>
    disclaimer?: { paragraphs?: LocalText }
    privacyPolicy?: FooterConfigLink
    termOfUse?: FooterConfigLink
}

const SHOW_FULL_FOOTER_PATHS = ['/', '/products', '/contact-us', '/app']
const HIDE_SUB_FOOTER_PATHS = ['/user']

const socialIcons: Record<string, string> = {
    facebook: facebookIcon,
    instagram: instagramIcon,
    pinterest: pinterestIcon,
    twitter: twitterIcon,
    youtube: youtubeIcon
}

const footerLinkSx = {
    color: 'white',
    fontFamily: 'Inter, NotoSansThai',
    fontStyle: 'normal',
    fontWeight: 400,
    textUnderlineOffset: '4px'
}

const getFooterSettings = (settings: unknown): FooterSettings | null => {
    if (!settings || typeof settings !== 'object') return null
    return settings as FooterSettings
}

export const FooterLayout = () => {
    const { pathname } = useLocation()
    const navigate = useNavigate()
    const { translate } = useTranslate()
    const footerSettings = getFooterSettings(useAtomValue(feelgreatSettingsAtom))
    const showFullFooter = SHOW_FULL_FOOTER_PATHS.includes(pathname)
    const hideSubFooter = HIDE_SUB_FOOTER_PATHS.includes(pathname)

    if (!footerSettings?.quickLinks?.items) return null

    const handleFooterLink = (item: FooterLinkItem) => {
        if (item.usageType === 'handleFunction') {
            const handlerName = localize(item.handleFunction)
            if (handlerName.includes('handleLinkToContact')) navigate('/contact-us')
            return
        }

        const internalPath = localize(item.internalLink ?? item.path)
        if (internalPath) navigate(internalPath)
    }

    return (
        <Box component="footer" id="fg_footer" data-testid="footer">
            {showFullFooter && (
                <>
                    <Box sx={{ width: '100%', height: '5px', backgroundColor: '#99BCDF' }} data-testid="footer-border" />
                    <FooterStyled className="site-footer" data-testid="footer-full">
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', lg: '1fr 3fr' },
                                gap: { xs: '41px', lg: '180px' }
                            }}
                            data-testid="footer-grid">
                            <SocialLinks data={footerSettings.social} />
                            <Box data-testid="footer-links-disclaimer">
                                <QuickLinks data={footerSettings.quickLinks.items} onNavigate={handleFooterLink} />
                                {footerSettings.disclaimer && (
                                    <Box
                                        sx={{
                                            border: '1px solid #CDDDED',
                                            borderRadius: '8px',
                                            marginTop: { sm: '40px' },
                                            padding: '16px 14px'
                                        }}
                                        data-testid="footer-disclaimer-box">
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: '#0B3B61',
                                                opacity: '0.5',
                                                lineHeight: '18px'
                                            }}
                                            data-testid="footer-disclaimer">
                                            {localize(footerSettings.disclaimer.paragraphs)}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </FooterStyled>
                </>
            )}
            {!hideSubFooter && (
                <Box
                    sx={{
                        boxSizing: 'border-box',
                        width: '100%',
                        backgroundColor: '#153862',
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'center',
                        padding: { xs: '22px 20px', sm: '22px 60px', lg: '22px 135px' },
                        justifyContent: 'space-between',
                        gap: { xs: '16px', md: '0px' }
                    }}
                    data-testid="footer-stack-typography">
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'white',
                            fontSize: { xs: '10px', sm: '12px' },
                            textAlign: 'center'
                        }}
                        data-testid="footer-typography">
                        &copy; {new Date().getFullYear()} {translate('copyright_unicity_international')}
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: { xs: '13px', sm: '22px' },
                            alignItems: 'center',
                            fontSize: { xs: '10px', sm: '12px' }
                        }}
                        data-testid="footer-stack-typography-privacy-policy">
                        <Box component="span" sx={footerLinkSx} data-testid="footer-protected-by-recaptcha">
                            {translate('protected_by_recaptcha')}
                        </Box>
                        <FooterMobileDivider />
                        {footerSettings.privacyPolicy && (
                            <FooterTextLink
                                href={localize(footerSettings.privacyPolicy.externalLink)}
                                target={footerSettings.privacyPolicy.openTarget}
                                rel={footerSettings.privacyPolicy.openTarget === '_blank' ? 'noreferrer' : undefined}
                                data-testid="footer-typography-privacy-policy">
                                {localize(footerSettings.privacyPolicy.title)}
                            </FooterTextLink>
                        )}
                        <FooterMobileDivider />
                        {footerSettings.termOfUse && (
                            <FooterTextLink
                                href={localize(footerSettings.termOfUse.externalLink)}
                                target={footerSettings.termOfUse.openTarget}
                                rel={footerSettings.termOfUse.openTarget === '_blank' ? 'noreferrer' : undefined}
                                data-testid="footer-typography-terms-of-use">
                                {localize(footerSettings.termOfUse.title)}
                            </FooterTextLink>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    )
}

const SocialLinks = ({ data }: { data?: FooterSettings['social'] }) => (
    <Stack data-testid="footer-social">
        <Box
            sx={{
                marginBottom: { xs: '20px', sm: '41px' },
                display: { xs: 'flex', lg: 'block' },
                alignItems: 'center',
                justifyContent: 'center'
            }}
            data-testid="footer-unicity-logo-box">
            <Box component="img" src={unicityLogo} alt="Unicity" data-testid="footer-unicity-logo" />
        </Box>
        <Box
            sx={{
                display: 'flex',
                gap: '32px',
                alignItems: 'center',
                justifyContent: { xs: 'center', lg: 'start' }
            }}
            data-testid="footer-social-icons">
            {Object.entries(data ?? {})
                .filter(([key, value]) => key !== 'isEnable' && value)
                .map(([key, value]) => {
                    const href = localize(value as LocalText)
                    const icon = socialIcons[key]
                    if (!href || !icon) return null

                    return (
                        <Box sx={{ cursor: 'pointer' }} key={key} data-testid={`footer-social-${key}`}>
                            <a href={href} target="_blank" rel="noreferrer">
                                <img src={icon} alt={`${key} social icon`} />
                            </a>
                        </Box>
                    )
                })}
        </Box>
    </Stack>
)

const QuickLinks = ({ data, onNavigate }: { data?: FooterQuickLinkGroup[]; onNavigate: (item: FooterLinkItem) => void }) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' }
        }}
        data-testid="footer-quick-links">
        {data?.map((mainItem, mainIndex) => {
            if (!mainItem.items?.length) return null

            return (
                <Box
                    key={`main-menu-${mainIndex}`}
                    sx={{
                        width: { xs: '100%', sm: '50%' },
                        marginBottom: { xs: '20px', sm: '0' }
                    }}
                    data-testid="footer-quick-link-group">
                    <Typography
                        variant="h2"
                        sx={{
                            fontFamily: 'Inter, NotoSansThai',
                            fontSize: '18px !important',
                            color: '#153862',
                            marginBottom: '20px'
                        }}
                        data-testid="footer-quick-link-title">
                        {localize(mainItem.title)}
                    </Typography>
                    <Box data-testid="footer-quick-link-items">
                        {mainItem.items.map((subItem, subIndex) => (
                            <Box key={`sub-menu-${subIndex}`} sx={{ marginBottom: '20px' }} data-testid="footer-quick-link-item">
                                {subItem.usageType === 'handleFunction' || subItem.internalLink || subItem.path ? (
                                    <Typography
                                        variant="body3"
                                        onClick={() => onNavigate(subItem)}
                                        sx={{
                                            fontFamily: 'Inter, NotoSansThai',
                                            fontSize: '18px !important',
                                            marginBottom: '20px',
                                            color: '#153862',
                                            cursor: 'pointer'
                                        }}
                                        data-testid="footer-quick-link-button">
                                        {localize(subItem.title)}
                                    </Typography>
                                ) : (
                                    <Typography
                                        variant="body3"
                                        sx={{
                                            fontFamily: 'Inter, NotoSansThai',
                                            fontSize: '18px !important',
                                            marginBottom: '20px'
                                        }}
                                        data-testid="footer-quick-link-anchor-wrapper">
                                        <a
                                            href={localize(subItem.externalLink)}
                                            target={subItem.openTarget}
                                            rel={subItem.openTarget === '_blank' ? 'noreferrer' : undefined}
                                            style={{ color: '#153862', textDecoration: 'none' }}>
                                            {localize(subItem.title)}
                                        </a>
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Box>
            )
        })}
    </Box>
)

const FooterMobileDivider = () => (
    <Box
        sx={{
            color: 'white',
            fontSize: '14px',
            display: { xs: 'block', sm: 'none' }
        }}
        data-testid="footer-mobile-divider">
        |
    </Box>
)

const FooterTextLink = styled('a')`
    color: white;
    font-family: Inter, NotoSansThai;
    font-style: normal;
    font-weight: 400;
    text-underline-offset: 4px;
`

const FooterStyled = styled('div')`
    padding: 60px 135px 40px;
    box-sizing: border-box;

    @media screen and (max-width: 599px) {
        padding: 60px 50px 40px !important;
    }

    @media screen and (max-width: 1199px) {
        padding: 60px 60px 40px;
    }
`
